/**
 * Height accordion - grid 0fr <-> 1fr (no JS measure).
 * Timing matches shell-panel-motion / ws-nav-expand-panel (200ms).
 * Gallery: vertical list expands (Sources, settings inline). Horizontal docks stay #panel-motion.
 */

import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { SHELL_PANEL_EASE, SHELL_PANEL_MOTION_MS } from '@/lib/shell-panel-motion'

export type SlideRevealProps = {
  open: boolean
  children: ReactNode
  className?: string
  /** Inner wrapper (overflow hidden lives here). */
  innerClassName?: string
  /** Override duration ms (default SHELL_PANEL_MOTION_MS = 200). */
  durationMs?: number
}

export function SlideReveal({
  open,
  children,
  className,
  innerClassName,
  durationMs = SHELL_PANEL_MOTION_MS,
}: SlideRevealProps) {
  const style = {
    gridTemplateRows: open ? '1fr' : '0fr',
    transitionProperty: 'grid-template-rows',
    transitionDuration: `${durationMs}ms`,
    transitionTimingFunction: SHELL_PANEL_EASE,
  } as CSSProperties

  return (
    <div
      className={cn('grid motion-safe:transition-[grid-template-rows]', className)}
      style={style}
      data-state={open ? 'open' : 'closed'}
      aria-hidden={!open}
    >
      <div className={cn('min-h-0 overflow-hidden', !open && 'pointer-events-none', innerClassName)}>
        <div
          className={cn(
            'motion-safe:transition-opacity',
            open ? 'opacity-100' : 'opacity-0',
          )}
          style={{
            transitionDuration: `${Math.max(durationMs - 20, 120)}ms`,
            transitionTimingFunction: SHELL_PANEL_EASE,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
